import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Module } from '@pc/core';
import { loadModuleFlagMap } from '@pc/db';
import { useActiveContext } from '../auth/ActiveContextProvider';
import { moduleRegistry } from './moduleRegistry';
import { useOrgBilling } from './useOrgBilling';

/**
 * Returns the modules enabled for the active org. Unknown/disabled module IDs
 * are filtered out. While flags are loading, returns the full registry so
 * navigation stays stable rather than flashing empty.
 *
 * The query key includes the active subscription's status + trial_end so
 * the module flag map automatically refetches when billing state changes
 * (trial expires, plan upgraded, subscription canceled). billing.rpc_org_get_billing
 * runs billing.fn_sync_org_module_flags on every call, so by the time
 * useOrgBilling sees a new status the flags are already correct in
 * core.org_module_flags -- we just need to invalidate this cache to
 * pick them up.
 */
export function useModules(): ReadonlyArray<Module> {
  const { activeOrgId } = useActiveContext();
  const billingQ = useOrgBilling();
  const billingStatus = billingQ.data?.subscription?.status ?? null;
  const trialEnd = billingQ.data?.subscription?.trial_end ?? null;

  const flagsQuery = useQuery({
    queryKey: ['moduleFlags', activeOrgId, billingStatus, trialEnd],
    queryFn: () => loadModuleFlagMap(activeOrgId as string),
    enabled: Boolean(activeOrgId),
  });

  return useMemo(() => {
    if (!flagsQuery.data) return moduleRegistry;
    return moduleRegistry.filter((m) => flagsQuery.data!.isEnabled(m.id));
  }, [flagsQuery.data]);
}
